import { ResponsibleGamingOptionData } from "../../../api/common/model/data/ResponsibleGamingOptionData";
import { ResponsibleGamingOptionType } from "../../../api/common/model/enum/ResponsibleGamingOptionType";
import { Dictionary } from "../../../util/collection/Dictionary";
import { NumberUtil } from "../../../util/number/NumberUtil";
/**
 * ResponsibleGamingData.ts
 *
 * Validates and Restricts player actions.
 */
export class ResponsibleGamingData {
    constructor(model) {
        this.model = model;
        this.dismissed = this.modified = this.hideInstructions = false;
        this.spinsRemaining = 0;
        this.trigger = ResponsibleGamingOptionType.MAXIMUM_LOSS;
        this.options = new Dictionary();
        this.activeOptions = new Dictionary();
        this.priceThreshold = 10;
        this.reset();
    }
    cancel() {
        this.updateSettings();
    }
    reset() {
        this.spinPrice = 0;
        this.resetActions();
        this.resetTrigger();
    }
    setSettings(settings) {
        this.settings = settings;
        this.hideInstructions = settings.isHideInstructions();
        this.updateSettings();
    }
    getActiveOptionsJSON() {
        const activeKeys = this.activeOptions.getKeys().map(key => key.getId());
        const activeValues = this.activeOptions.getValues().map(val => val.getValue());
        let ret = {};
        activeKeys.forEach((key, index) => {
            ret[key] = activeValues[index];
        });
        return JSON.stringify(ret);
    }
    updateOption(option, selected) {
        this.modified = true;
        let type = option.getType();
        if (selected) {
            this.activeOptions.setValue(type, option);
            this.model.dispatchKPI("TITAN_STATE", `PLAY_CONTROLS_${option.getType().getId()}_SELECTED`, `${option.getValue()}`);
        }
        else {
            this.activeOptions.remove(type);
            this.model.dispatchKPI("TITAN_STATE", `PLAY_CONTROLS_${option.getType().getId()}_REMOVED`, `${option.getValue()}`);
        }
    }
    updateSettings() {
        this.modified = false;
        this.activeOptions.clear();
        this.spinPrice = 0;
        if (this.hasSettings()) {
            for (let type of ResponsibleGamingOptionType.getTypes()) {
                this.updateSetting(type);
            }
        }
    }
    updateSetting(type) {
        if (this.settings.hasSetting(type)) {
            this.activeOptions.setValue(type, this.settings.getSetting(type));
        }
    }
    resetActions() {
        this.priceConfirm = false;
        this.priceCancel = false;
    }
    resetTrigger() {
        this.trigger = null;
    }
    update(spinsRemaining, spinsAvailable) {
        if (this.isOptionActive(ResponsibleGamingOptionType.EXTRA_SPIN)) {
            let spinsTaken = spinsAvailable - spinsRemaining;
            let option = this.getActiveOption(ResponsibleGamingOptionType.EXTRA_SPIN);
            let optionRemaining = Math.max(0, option.getValue() - spinsTaken);
            spinsRemaining = Math.min(spinsRemaining, optionRemaining);
        }
        this.spinsRemaining = spinsRemaining;
    }
    completeGame(gameInstanceId) {
        this.gameInstanceId = gameInstanceId;
    }
    /* Validation Logic */
    isLimitsValid(stake, spinPrice, totalStake, totalWin) {
        this.spinPrice = spinPrice;
        let valid = true;
        if (this.hasOptions()) {
            this.resetTrigger();
            this.validateOptions(stake, spinPrice, totalStake, totalWin);
            valid = this.isValid();
            this.resetSpins(valid);
        }
        return valid;
    }
    resetSpins(valid) {
        this.spinsRemaining = valid ? this.spinsRemaining : 0;
    }
    isThresholdValid(stake, spinPrice) {
        this.spinPrice = spinPrice;
        let valid = !this.isSpinAbovePurchaseSpinConfirmValue(stake, spinPrice);
        // Atlas Spin Confirm value
        const purchaseSpinConfirmValue = this.getPurchaseSpinConfirmValue();
        // UKGC
        if (isNaN(purchaseSpinConfirmValue) && this.isEnabled() && this.priceThreshold > 0) {
            const spinPriceThreshold = stake * this.priceThreshold;
            if (spinPrice > spinPriceThreshold) {
                valid = false;
            }
        }
        return valid;
    }
    isSpinAbovePurchaseSpinConfirmValue(stake, spinPrice) {
        const purchaseSpinConfirmValue = this.getPurchaseSpinConfirmValue();
        let spinPriceThreshold = stake * purchaseSpinConfirmValue;
        return spinPrice > spinPriceThreshold && spinPrice > 0;
    }
    validateOptions(stake, spinPrice, totalStake, totalWin) {
        for (let option of this.activeOptions.getValues()) {
            this.validateOption(option, stake, spinPrice, totalStake, totalWin);
        }
    }
    validateOption(option, stake, spinPrice, totalStake, totalWin) {
        if (option.isExtraSpin()) {
            this.validateExtraSpin(option);
        }
        else if (option.isSpinPrice()) {
            this.validateSpinPrice(option, stake, spinPrice);
        }
        else if (option.isSingleGameStake()) {
            this.validateSingleGameStakePrice(option, stake, spinPrice, totalStake);
        }
        else if (option.isMaximumLoss()) {
            this.validateMaximumLoss(option, stake, spinPrice, totalStake, totalWin);
        }
    }
    validateExtraSpin(option) {
        if (this.spinsRemaining == 0) {
            this.stop(option.getType());
        }
    }
    validateSpinPrice(option, stake, spinPrice) {
        let optionValue = option.getValue();
        let threshold = this.round(stake * optionValue);
        if (spinPrice > threshold) {
            this.stop(option.getType());
        }
    }
    validateSingleGameStakePrice(option, stake, spinPrice, totalStake) {
        let potentialStake = totalStake + spinPrice;
        let optionValue = option.getValue();
        let threshold = this.round(stake * optionValue);
        if (potentialStake > threshold) {
            this.stop(option.getType());
        }
    }
    validateMaximumLoss(option, stake, spinPrice, totalStake, totalWin) {
        let potentialStake = totalStake + spinPrice;
        let potentialLoss = this.round((potentialStake - totalWin) / stake);
        let optionValue = option.getValue();
        if (potentialLoss > optionValue) {
            this.stop(option.getType());
        }
    }
    stop(type) {
        const valueString = this.activeOptions.getValue(type).getValue().toString();
        switch (type) {
            case ResponsibleGamingOptionType.EXTRA_SPIN:
                this.model.dispatchKPI("TITAN_STATE", "PLAY_CONTROLS_EXTRA_SPIN_TRIGGERED", valueString);
                break;
            case ResponsibleGamingOptionType.SPIN_PRICE:
                this.model.dispatchKPI("TITAN_STATE", "PLAY_CONTROLS_SPIN_PRICE_TRIGGERED", valueString);
                break;
            case ResponsibleGamingOptionType.SINGLE_GAME_STAKE:
                this.model.dispatchKPI("TITAN_STATE", "PLAY_CONTROLS_SINGLE_GAME_STAKE_TRIGGERED", valueString);
                break;
            case ResponsibleGamingOptionType.MAXIMUM_LOSS:
                this.model.dispatchKPI("TITAN_STATE", "PLAY_CONTROLS_MAXIMUM_LOSS_TRIGGERED", valueString);
                break;
            default:
                // No action needed for other types
                break;
        }
        this.trigger = type.compare(this.trigger);
    }
    addOption(type, values) {
        let options = [];
        for (let value of values) {
            options.push(new ResponsibleGamingOptionData(type, value));
        }
        this.options.setValue(type, options);
    }
    isModified() {
        return this.modified;
    }
    isPriceConfirm() {
        return this.priceConfirm;
    }
    isPriceCancel() {
        return this.priceCancel;
    }
    isOptionActive(type) {
        return this.activeOptions.containsKey(type);
    }
    isValid() {
        return this.trigger == null;
    }
    isShowInstructions() {
        return this.isEnabled() && !this.hideInstructions && !this.dismissed;
    }
    isHideInstructions() {
        return this.hideInstructions;
    }
    isEnabled() {
        const operatorConfig = this.model.getOperatorConfig();
        return operatorConfig.isPlayControlsEnabled();
    }
    hasTrigger() {
        return this.trigger != null;
    }
    hasSettings() {
        return this.settings != null;
    }
    hasOptions() {
        return this.activeOptions.size() > 0;
    }
    setDismissed(value) {
        this.dismissed = value;
    }
    setPriceThreshold(value) {
        this.priceThreshold = value;
    }
    setPriceConfirm(value) {
        this.priceConfirm = value;
    }
    setPriceCancel(value) {
        this.priceCancel = value;
    }
    setHideInstructions(value) {
        this.hideInstructions = value;
        this.modified = true;
    }
    getTrigger() {
        return this.trigger;
    }
    getGameInstanceId() {
        return this.gameInstanceId;
    }
    getOptions(type) {
        return this.options.getValue(type);
    }
    getPurchaseSpinsRemaining() {
        return this.spinsRemaining;
    }
    getActiveOption(type) {
        return this.activeOptions.getValue(type);
    }
    getSpinPrice() {
        return this.spinPrice;
    }
    setPurchaseSpinConfirmValue(value) {
        this.purchaseSpinConfirmValue = value;
    }
    getPurchaseSpinConfirmValue() {
        const operatorConfig = this.model.getOperatorConfig();
        return operatorConfig.getPurchaseSpinConfirmValue();
    }
    round(value) {
        return NumberUtil.round(value, 2);
    }
}
//# sourceMappingURL=ResponsibleGamingData.js.map